/**
 * Gerenciamento de redirecionamento entre apps
 * 
 * Fluxo:
 * 1. Usuário acessa grh.ecxpay.com.br sem sessão
 * 2. GRH redireciona para auth.ecxpay.com.br/login?redirect=https://grh.ecxpay.com.br/dashboard
 * 3. Auth faz login e redireciona de volta para o GRH
 */

const REDIRECT_KEY = 'ecx_redirect_url'

/**
 * Salva a URL de redirect
 */
export const saveRedirectUrl = (url: string): void => {
  sessionStorage.setItem(REDIRECT_KEY, url)
}

/**
 * Obtém a URL de redirect da query string ou do sessionStorage
 */
export const getRedirectUrl = (): string | null => {
  // Primeiro tenta da query string
  const urlParams = new URLSearchParams(window.location.search)
  const redirectFromQuery = urlParams.get('redirect')
  
  if (redirectFromQuery) {
    // Valida se é um domínio permitido
    if (isAllowedDomain(redirectFromQuery)) {
      saveRedirectUrl(redirectFromQuery)
      return redirectFromQuery
    }
  }
  
  // Se não, tenta do sessionStorage
  const saved = sessionStorage.getItem(REDIRECT_KEY)
  if (saved && isAllowedDomain(saved)) {
    return saved
  }
  
  return null
}

/**
 * Limpa a URL de redirect
 */
export const clearRedirectUrl = (): void => {
  sessionStorage.removeItem(REDIRECT_KEY)
}

/**
 * Verifica se o domínio é permitido para redirect
 */
export const isAllowedDomain = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url)
    const hostname = parsedUrl.hostname
    
    // Lista de domínios permitidos
    const allowedDomains = [
      // Desenvolvimento
      'localhost',
      '127.0.0.1',
      'ecx.local',          // nginx local para testar HttpOnly cookies
      'api.ecx.local',      // API Multibenefícios local
      'api-corp.ecx.local', // API Corp local
      // Produção
      'ecxpay.com.br',
      'grh.ecxpay.com.br',
      'corp.ecxpay.com.br',
      'auth.ecxpay.com.br',
      // Staging
      'staging.ecxpay.com.br',
      'staging-auth.ecxpay.com.br',
      'staging-corp.ecxpay.com.br',
    ]
    
    // Verifica se é exatamente um dos domínios ou subdomínio
    return allowedDomains.some(domain => {
      return hostname === domain || hostname.endsWith(`.${domain}`)
    })
  } catch {
    return false
  }
}

/**
 * Redireciona para um app específico
 */
export const redirectToApp = (url: string): void => {
  clearRedirectUrl()
  window.location.href = url
}

/**
 * Gera a URL de login com redirect
 */
export const getLoginUrl = (redirectTo: string): string => {
  const authBaseUrl = import.meta.env.VITE_AUTH_URL || 'https://auth.ecxpay.com.br'
  const encoded = encodeURIComponent(redirectTo)
  return `${authBaseUrl}/login?redirect=${encoded}`
}

